import Box from '@/common/components/Box'
import ResumeItem from '@/common/components/ResumeItem'
import { cn } from '@/common/utils/cn'
import { formatCurrency } from '@/common/utils/formatters'
import FinancialReviewEntrySimpleEntryApprove from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/FinancialReviewEntry/SimpleEntry/Approve'
import FinancialReviewEntrySimpleEntryReject from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/FinancialReviewEntry/SimpleEntry/Reject'
import {
  MaintenanceForwardingSectionStyle,
  maintenanceForwardingSectionStyles,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingSectionStyles.constant'
import { maintenanceForwardingsDirectiveEntryKlasses } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.constant'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/constants/maintenanceForwardingsDirectiveEntryKlasses.type'
import { MaintenanceForwardingStatus } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import useGetDirectiveEntryObjectivesSummary from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useGetDirectiveEntryObjectivesSummary'
import { useMemo } from 'react'

const MaintenanceWorkflowForwardingFinancialReviewEntrySection = () => {
  const { workflow, forwarding, directiveEntryKlass, noActions } =
    useForwardingTabContext<MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry>()

  const directiveEntryObjectivesSummary = useGetDirectiveEntryObjectivesSummary()

  const isOnGoingFinancialReview = useMemo(() => {
    return (
      workflow?.current_forwarding?.id === forwarding?.id &&
      forwarding?.status === MaintenanceForwardingStatus.OnGoingFinancialReview
    )
  }, [workflow?.current_forwarding?.id, forwarding?.status])

  const isFinancialReviewApproved = useMemo(() => {
    return (
      !isOnGoingFinancialReview &&
      workflow?.current_forwarding?.id === forwarding?.id &&
      workflow?.current_forwarding?.financial_review_entry?.approved_at
    )
  }, [
    isOnGoingFinancialReview,
    workflow?.current_forwarding?.id,
    workflow?.current_forwarding?.financial_review_entry?.approved_at,
  ])

  const sectionStyle = useMemo(() => {
    if (isFinancialReviewApproved) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Completed]
    }

    if (isOnGoingFinancialReview) {
      return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Pending]
    }

    return maintenanceForwardingSectionStyles[MaintenanceForwardingSectionStyle.Default]
  }, [isFinancialReviewApproved, isOnGoingFinancialReview])

  return (
    <Box className={cn('p-4 flex flex-col gap-4', sectionStyle)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <p className="text-lg font-semibold">Análise financeira</p>

          {isFinancialReviewApproved && (
            <div>
              <p className="text-valid text-xs">Aprovado</p>
            </div>
          )}
        </div>

        {isOnGoingFinancialReview && !noActions && (
          <div className="flex items-center gap-3">
            <FinancialReviewEntrySimpleEntryReject />

            <FinancialReviewEntrySimpleEntryApprove />
          </div>
        )}
      </div>

      <div className="flex gap-3">
        <ResumeItem
          label={`Custo total de ${directiveEntryKlass && maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.label}`}
          value={
            (forwarding?.directive_entry?.total_value &&
              formatCurrency(parseFloat(forwarding?.directive_entry?.total_value))) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />

        <ResumeItem
          label="Custo em Peças"
          value={
            (directiveEntryObjectivesSummary.piecesTotalValue !== undefined &&
              formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue)) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />

        <ResumeItem
          label="Custo em Serviços"
          value={
            (directiveEntryObjectivesSummary.activitiesTotalValue !== undefined &&
              formatCurrency(directiveEntryObjectivesSummary.activitiesTotalValue)) ||
            '--'
          }
          className="text-xs"
          valueClassName="font-bold text-sm"
        />
      </div>
    </Box>
  )
}

export default MaintenanceWorkflowForwardingFinancialReviewEntrySection
